import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAttachPaymentProof } from '../hooks';
import type { Bill } from '../types';

interface Props {
  bill: Bill | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Attach a payment proof to a bill from the /acc/bills list.
 *
 * The user pastes either a DMS UUID (preferred when the doc already lives in
 * DMS — e.g. the auto-intaken RBC PayEdge order) or a Drive URL (preferred for
 * docs filed in the supplier's Drive folder). Backend routes to the latest
 * acc_bill_payments row, or creates an acc_bill_documents row if no payment
 * exists yet.
 */
export default function AttachProofDialog({ bill, open, onOpenChange }: Props) {
  const [dmsId, setDmsId] = useState('');
  const [gdriveUrl, setGdriveUrl] = useState('');
  const mutation = useAttachPaymentProof();

  useEffect(() => {
    if (open) {
      setDmsId(bill?.payment_proof_dms_id ?? '');
      setGdriveUrl(bill?.payment_proof_gdrive_url ?? '');
    }
  }, [open, bill]);

  if (!bill) return null;

  const dmsClean = dmsId.trim();
  const urlClean = gdriveUrl.trim();
  const canSubmit = (dmsClean.length > 0 || urlClean.length > 0) && !mutation.isPending;

  const submit = async () => {
    if (!canSubmit) return;
    await mutation.mutateAsync({
      billId: bill.id,
      input: {
        dmsDocumentId: dmsClean || null,
        gdriveUrl: urlClean || null,
      },
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Attach payment proof</DialogTitle>
          <DialogDescription>
            Link a proof (RBC PayEdge confirmation, bank-debit statement excerpt, cheque image) to{' '}
            <span className="font-mono">{bill.doc_number ?? bill.id}</span> — {bill.vendor_name ?? 'vendor'}.
            Provide either a DMS UUID or a Drive URL (or both).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="dms-id">DMS document UUID</Label>
            <Input
              id="dms-id"
              placeholder="e.g. 45b67a3d-3eea-4393-96b7-09938830c102"
              value={dmsId}
              onChange={(e) => setDmsId(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">From DMS — the document UUID shown after upload.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="gdrive-url">Drive URL</Label>
            <Input
              id="gdrive-url"
              placeholder="https://drive.google.com/file/d/…"
              value={gdriveUrl}
              onChange={(e) => setGdriveUrl(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">From Drive — the supplier-folder file shareable link.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit}>
            {mutation.isPending ? 'Attaching…' : 'Attach'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
